import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import moment from 'moment-timezone';
import React from 'react';
import { Bar } from 'react-chartjs-2';
import { useSelector } from 'react-redux';
import { Link as RouterLink } from 'react-router-dom';
import { useTheme } from 'styled-components';
import { type Days, useGetOrganisationInsightsSummary } from '../../../../../../app/ApiGen';
import { DateLookbackSelect } from '../../../../../../components/DateLookbackSelect';
import { DataErrorHandler } from '../../../../../../components/ErrorHandler';
import { Card, Loading } from '../../../../../../design-system';
import { fullStateSelector } from '../../../../../../store/root';
import { TabContext } from './Context';
import { InsightTotals } from './InsightTotals';
import { ChartWrapper, Label, Tab, Tabs, Value } from './styled';
import { getChartData, getInsightTotals } from './utils';

export const OrganisationSummary: React.FC<{ orgSlug: string; orgId: string }> = ({ orgSlug, orgId }) => {
  const { palette } = useTheme();

  const { isAdmin, isEvnexAdmin, isFleetOperator } = useSelector(fullStateSelector);

  const [tab, setTab] = React.useState(0);
  const [lookback, setLookback] = React.useState<Days>(7);

  const { loading, error, data, refetch } = useGetOrganisationInsightsSummary({
    id: orgId,
    queryParams: { days: lookback, 'tz-offset': parseInt(`${moment().utcOffset() / 60}`, 10) },
  });

  if (loading) {
    return (
      <Card style={{ height: '100%' }}>
        <Loading py={3} height="100%" />
      </Card>
    );
  }

  if (error || !data) {
    return (
      <DataErrorHandler
        description="Unable to load organisation summary"
        refetch={refetch}
        error={error}
        type="embedded"
      />
    );
  }

  const rawTotals = getInsightTotals(data.data);
  const totals = new InsightTotals({
    powerUsage: rawTotals.powerUsage,
    sessions: rawTotals.sessions,
    cost: rawTotals.cost,
    duration: rawTotals.duration,
    carbonOffset: rawTotals.carbonOffset,
    totalCarbonUsage: rawTotals.totalCarbonUsage,
  });

  return (
    <Card
      style={{ height: '100%' }}
      footer={
        <>
          <DateLookbackSelect selected={lookback} onSelect={setLookback} />
          <Box ml="auto" />
          {(isEvnexAdmin || isAdmin || isFleetOperator) && (
            <Button
              component={RouterLink}
              to={`/organisations/${orgSlug}/reports/sessions`}
              color="primary"
              variant="text"
              size="small"
              endIcon={<ChevronRightIcon />}
            >
              More reports
            </Button>
          )}
        </>
      }
    >
      <TabContext.Provider value={{ selected: tab }}>
        <Tabs>
          <Tab tabId={0} tabIndex={0} onClick={() => setTab(0)}>
            <Label>Electricity usage</Label>
            <Value>
              {totals.powerUsage} <small>kWh</small>
            </Value>
          </Tab>
          <Tab tabId={1} tabIndex={0} onClick={() => setTab(1)}>
            <Label>Electricity cost</Label>
            <Value>${totals.cost}</Value>
          </Tab>
          <Tab tabId={2} tabIndex={0} onClick={() => setTab(2)}>
            <Label>Sessions</Label>
            <Value>{totals.sessions}</Value>
          </Tab>
          <Tab tabId={3} tabIndex={0} onClick={() => setTab(3)}>
            <Label>Carbon offset</Label>
            <Value>
              {totals.formattedCarbonOffset} <small>kg</small>
            </Value>
          </Tab>
          {totals.formattedCarbonUsage && (
            <Tab tabId={4} tabIndex={0} onClick={() => setTab(4)}>
              <Label>Carbon use</Label>
              <Value>
                {totals.formattedCarbonUsage.value} <small>{totals.formattedCarbonUsage.unit}</small>
              </Value>
            </Tab>
          )}
        </Tabs>
        <ChartWrapper>
          <Bar
            options={{
              aspectRatio: 2.75,
              layout: { padding: 16 },
              plugins: {
                legend: { display: false },
              },
              scales: {
                y: {
                  beginAtZero: true,
                },
              },
            }}
            data={getChartData(tab, data.data, palette.primary.main)}
          />
        </ChartWrapper>
      </TabContext.Provider>
    </Card>
  );
};
